import { getTraceId } from './traceContext'

export interface SseMessage {
  event: string
  data: string
  id?: string
}

export interface SseStreamErrorInput {
  status?: number
  message: string
  details?: unknown
  traceId?: string
}

export interface FetchSseStreamParams {
  url: string
  headers?: HeadersInit
  signal?: AbortSignal
  onMessage: (
    message: SseMessage
  ) => boolean | undefined | Promise<boolean | undefined>
}

export class SseStreamError extends Error {
  status?: number
  details?: unknown
  traceId?: string

  constructor({ status, message, details, traceId }: SseStreamErrorInput) {
    super(message)
    this.name = 'SseStreamError'
    this.status = status
    this.details = details
    this.traceId = traceId
  }
}

interface SseParserState {
  lastEventId?: string
}

function parseSseMessage(
  frame: string,
  parserState: SseParserState
): SseMessage | null {
  const lines = frame.split(/\r?\n/)
  let event = 'message'
  const dataLines: string[] = []

  for (const line of lines) {
    if (!line || line.startsWith(':')) {
      continue
    }

    const separatorIndex = line.indexOf(':')
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex)
    const rawValue = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1)
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue

    switch (field) {
      case 'event': {
        event = value
        break
      }
      case 'data': {
        dataLines.push(value)
        break
      }
      case 'id': {
        parserState.lastEventId = value
        break
      }
    }
  }

  if (dataLines.length === 0) {
    return null
  }

  return {
    event,
    data: dataLines.join('\n'),
    id: parserState.lastEventId,
  }
}

export function parseSseChunkBuffer(
  buffer: string,
  parserState: SseParserState = {}
): {
  messages: SseMessage[]
  remainder: string
} {
  const normalized = buffer.replace(/\r\n/g, '\n')
  const frames = normalized.split('\n\n')
  const remainder = frames.pop() || ''
  const messages = frames
    .map((frame) => parseSseMessage(frame, parserState))
    .filter((message): message is SseMessage => message !== null)

  return {
    messages,
    remainder,
  }
}

async function readErrorResponse(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) {
    return null
  }

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function assertSseResponse(response: Response): Promise<void> {
  // OTel's fetch instrumentation records the trace-id (via rememberTraceId) in a
  // `.then()` on the original fetch promise, which the microtask queue runs
  // before this awaiting caller receives the Response — so the id is already
  // available here. Keep trace-id reads on the same microtask as the resolved
  // Response; don't defer past an extra `await`.
  const traceId = getTraceId(response)

  if (!response.ok) {
    const details = await readErrorResponse(response)
    throw new SseStreamError({
      status: response.status,
      message: `SSE request failed with HTTP ${response.status}.`,
      details,
      traceId,
    })
  }

  const contentType = response.headers.get('content-type')
  if (!contentType?.toLowerCase().includes('text/event-stream')) {
    throw new SseStreamError({
      status: response.status,
      message: 'Response is not an SSE stream.',
      details: contentType,
      traceId,
    })
  }

  if (!response.body) {
    throw new SseStreamError({
      message: 'SSE response body was empty.',
    })
  }
}

export async function fetchSseStream({
  url,
  headers,
  signal,
  onMessage,
}: FetchSseStreamParams): Promise<void> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        accept: 'text/event-stream',
        ...headers,
      },
      signal,
    })

    await assertSseResponse(response)

    const reader = response.body!.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    const parserState: SseParserState = {}

    while (true) {
      const { value, done } = await reader.read()

      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })
      const parsed = parseSseChunkBuffer(buffer, parserState)
      buffer = parsed.remainder

      for (const message of parsed.messages) {
        if ((await onMessage(message)) === false) {
          await reader.cancel()
          return
        }
      }
    }

    buffer += decoder.decode()
    const parsed = parseSseChunkBuffer(`${buffer}\n\n`, parserState)

    for (const message of parsed.messages) {
      if ((await onMessage(message)) === false) {
        return
      }
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return
    }

    throw error
  }
}
